import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { Visit } from '../createVisit/types'

interface VisitDetailsState {
  selectedVisit: Visit | null
  loading: boolean
  refresh: boolean
}

const initialState: VisitDetailsState = {
  selectedVisit: null,
  loading: false,
  refresh: false,
}

const IpdVisitDetailsSlice = createSlice({
  name: 'ipdvisitDetails',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // Set refresh state
    setRefresh: (state, action: PayloadAction<boolean>) => {
      state.refresh = action.payload
    },

    // Set selected visit
    setSelectedVisit: (state, action: PayloadAction<Visit | null>) => {
      state.selectedVisit = action.payload
    },

    // Get selected visit (this is just a helper, actual state is accessed via selectors)
    getSelectedVisit: (state) => {
      // This reducer doesn't modify state, just for semantic consistency
      // The actual data retrieval happens through selectors
      return state
    },

    // Clear selected visit
    clearSelectedVisit: (state) => {
      state.selectedVisit = null
    },

    // Update selected visit data
    updateSelectedVisit: (state, action: PayloadAction<Partial<Visit>>) => {
      if (state.selectedVisit) {
        state.selectedVisit = {
          ...state.selectedVisit,
          ...action.payload,
        }
      }
    },

    // Set visit data with loading state
    setVisitData: (
      state,
      action: PayloadAction<{
        visit: Visit | null
        loading?: boolean
        refresh?: boolean
      }>
    ) => {
      state.selectedVisit = action.payload.visit
      if (action.payload.loading !== undefined) {
        state.loading = action.payload.loading
      }
      if (action.payload.refresh !== undefined) {
        state.refresh = action.payload.refresh
      }
    },

    // Reset all visit details to initial state
    resetVisitDetails: (state) => {
      state.selectedVisit = null
      state.loading = false
      state.refresh = false
    },
  },
})

export const {
  setLoading,
  setRefresh,
  setSelectedVisit,
  getSelectedVisit,
  clearSelectedVisit,
  updateSelectedVisit,
  setVisitData,
  resetVisitDetails,
} = IpdVisitDetailsSlice.actions

// Selectors for easy access to state values
export const selectVisitDetails = (state: {
  visitDetails: VisitDetailsState
}) => state.visitDetails

export const selectSelectedVisit = (state: {
  visitDetails: VisitDetailsState
}) => state.visitDetails.selectedVisit

export const selectVisitLoading = (state: {
  visitDetails: VisitDetailsState
}) => state.visitDetails.loading

export const selectVisitRefresh = (state: {
  visitDetails: VisitDetailsState
}) => state.visitDetails.refresh

// Helper selector to check if a visit is selected
export const selectHasSelectedVisit = (state: {
  visitDetails: VisitDetailsState
}) => state.visitDetails.selectedVisit !== null

export default IpdVisitDetailsSlice.reducer
